import React, { useState } from 'react';
import { DialogTitle, DialogContent, DialogActions, TextField, Typography } from '@material-ui/core';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { genAttributes } from '@onward-delivery/core';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { asDateInTZ } from '@/utilities/convertToISO';
import RescheduleReasonFields from '@/components/Reschedule/RescheduleReasonFields';
import useRescheduleReason from '@/components/Reschedule/useRescheduleReason';

function getWarning(order) {
    if (!order) return null;

    const hasActiveMappings = (order?.routes || []).some(
        (m) => m?.mapping_id && (m?.duplicate_override == null || m?.duplicate_override === 'DISCARD')
    );

    if (order.event_state === 'END') {
        return 'This order is marked as delivered. Rescheduling will revert the delivery and clear the delivery window.';
    }
    if (order.event_state === 'CD_COMPLETE') {
        return hasActiveMappings
            ? 'This order is assigned to a route that is currently in progress. The stop will remain on the route — notify the driver to skip it.'
            : 'This order is currently out for delivery. The delivery date will be updated and the order reset to claimed.';
    }
    if (hasActiveMappings) {
        const activeMapping = (order?.routes || []).find(
            (m) => m?.mapping_id && (m?.duplicate_override == null || m?.duplicate_override === 'DISCARD')
        );
        const routeStatus = activeMapping?.route?.status;

        if (routeStatus === 'inProgress') {
            return 'This order is assigned to a route that is currently in progress. The stop will remain on the route — notify the driver to skip it.';
        }
        if (routeStatus === 'planning') {
            return 'This order is assigned to a route that hasn\'t been submitted yet. The route will be flagged for re-optimization.';
        }
        return 'This order is assigned to a submitted route. The route will be flagged for re-optimization and will need to be resubmitted after removing this stop.';
    }
    return null;
}

export default function RescheduleOrderModal({ open, onClose, onConfirm, loading = false, order }) {
    const { reason, notes, reset: resetReason, isValid, fieldProps } = useRescheduleReason();
    const [deliveryDate, setDeliveryDate] = useState('');

    const canConfirm = isValid && !!deliveryDate && !loading;
    const warning = getWarning(order);

    const getLocalTimeDate = (date) => {
        if (!order || !date) return date;
        const { zip } = genAttributes(order);
        const tz = order[zip] ? zipcode_to_timezone.lookup(order[zip]) : 'America/New_York';
        return asDateInTZ(date, tz).toISOString();
    };

    const reset = () => {
        resetReason();
        setDeliveryDate('');
    };

    const handleConfirm = () => {
        onConfirm({ reason, notes, deliveryDate: getLocalTimeDate(deliveryDate) }).then(() => {
            reset();
            onClose();
        });
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const minDate = new Date().toISOString().split('T')[0];

    return (
        <NavResponsiveModal open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Reschedule Order</DialogTitle>
            <DialogContent>
                {warning && (
                    <Typography variant="body2" color="error" style={{ marginBottom: '24px' }}>
                        {warning}
                    </Typography>
                )}
                <TextField
                    type="date"
                    label="New delivery date (required)"
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: minDate }}
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    disabled={loading}
                    style={{ marginBottom: '24px' }}
                />
                <RescheduleReasonFields {...fieldProps} disabled={loading} />
            </DialogContent>
            <DialogActions style={{ justifyContent: 'space-between', padding: '16px 24px' }}>
                <SecondaryButton onClick={handleClose} disabled={loading}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleConfirm} disabled={!canConfirm}>
                    Reschedule
                </PrimaryButton>
            </DialogActions>
        </NavResponsiveModal>
    );
}
